import React from 'react';

const SegmentHeatmap = ({ data }) => {
  const segments = ['FO', 'EQ', 'MF', 'CDS', 'COM'];
  const segmentNames = {
    'FO': 'Futures & Options',
    'EQ': 'Equity',
    'MF': 'Mutual Funds',
    'CDS': 'Currency Derivatives',
    'COM': 'Commodities'
  };

  const formatCurrency = (value) => {
    if (Math.abs(value) >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (Math.abs(value) >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (Math.abs(value) >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    } else {
      return `₹${value.toFixed(0)}`;
    }
  };

  // Calculate color intensity based on P&L values
  const getColorIntensity = (value, maxValue) => {
    if (maxValue === 0) return 0;
    return Math.min(Math.abs(value) / maxValue, 1);
  };

  // Find max absolute value for normalization
  const allValues = data.flatMap(row => 
    segments.map(segment => Math.abs(row[`${segment}_total_pnl`] || 0))
  );
  const maxValue = Math.max(...allValues);

  const getCellStyle = (value) => {
    const intensity = getColorIntensity(value, maxValue);
    
    if (value > 0) {
      return {
        backgroundColor: `rgba(21, 128, 61, ${0.1 + intensity * 0.6})`,
        color: intensity > 0.4 ? 'white' : '#15803d'
      };
    } else if (value < 0) {
      return {
        backgroundColor: `rgba(185, 28, 28, ${0.1 + intensity * 0.6})`,
        color: intensity > 0.4 ? 'white' : '#b91c1c'
      };
    } else {
      return {
        backgroundColor: '#f8fafc',
        color: '#64748b'
      };
    }
  };

  return (
    <div className="chart-container">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Segment Performance Heatmap
        </h3>
        <p className="text-gray-600">
          Annual P&L by trading segment. Intensity indicates relative performance.
        </p>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[900px]">
          {/* Header */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            <div className="p-3 font-semibold text-gray-700">Financial Year</div>
            <div className="p-3 font-semibold text-gray-700 text-center">
              <div className="text-sm font-bold">Total</div>
              <div className="text-xs text-gray-500">All Segments</div>
            </div>
            {segments.map(segment => (
              <div key={segment} className="p-3 font-semibold text-gray-700 text-center">
                <div className="text-sm font-bold">{segment}</div>
                <div className="text-xs text-gray-500">{segmentNames[segment]}</div>
              </div>
            ))}
          </div>

          {/* Data Rows */}
          {data.map((row, index) => {
            const totalPnl = row.total_pnl || 0;
            const totalStyle = getCellStyle(totalPnl);
            
            return (
              <div key={index} className="grid grid-cols-7 gap-2 mb-2">
                <div className="p-4 bg-gray-50 rounded-lg font-semibold text-gray-900 flex items-center">
                  {row.fy}
                </div>
                {/* Total P&L Column */}
                <div
                  className="p-4 rounded-lg text-center font-semibold transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer border-2 border-gray-300"
                  style={totalStyle}
                  title={`Total P&L: ${formatCurrency(totalPnl)}`}
                >
                  <div className="text-sm font-bold">
                    {formatCurrency(totalPnl)}
                  </div>
                </div>
                {segments.map(segment => {
                  const value = row[`${segment}_total_pnl`] || 0;
                  const style = getCellStyle(value);
                  
                  return (
                    <div
                      key={segment}
                      className="p-4 rounded-lg text-center font-semibold transition-all duration-200 hover:scale-105 hover:shadow-md cursor-pointer"
                      style={style}
                      title={`${segmentNames[segment]}: ${formatCurrency(value)}`}
                    >
                      <div className="text-sm font-bold">
                        {formatCurrency(value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-profit-500 rounded"></div>
          <span className="text-gray-600">Profit</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-loss-500 rounded"></div>
          <span className="text-gray-600">Loss</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gray-200 rounded"></div>
          <span className="text-gray-600">No Activity</span>
        </div>
      </div>
    </div>
  );
};

export default SegmentHeatmap;